<script setup lang="ts">
import type  { PropType } from 'vue'
import { ref, computed } from 'vue'

interface ExploreOthersItem {
    desc: string,
    icon: string,
    path?: string,
    bg?: string
}

const props = defineProps({
    title: {
        type: String,
        default: '标题'
    },
    showTypes: {
        type: Array as PropType<ExploreOthersItem[]>,
        default: () => []
    },
    customCategory: {
        type: String,
        default: ''
    }
})

const route = useRoute()
const localizedPath = (path: string) => {
    if (!path.startsWith('/')) return path
    const locale = route.path.match(/^\/([a-z]{2})(?=\/|$)/i)?.[1]
    const pathWithoutLocale = path.replace(/^\/[a-z]{2}(?=\/|$)/i, '') || '/'
    return locale && locale !== 'en'
        ? `/${locale}${pathWithoutLocale}`
        : pathWithoutLocale
}

// 定义偏移量
const currentIndex = ref(0)

// 添加禁用状态判断
const canGoLeft = computed(() => currentIndex.value > 0)
const canGoRight = computed(() => currentIndex.value < 1) // 只有2页,最大index是1

const goLeft = () => {
    if (canGoLeft.value) {
        currentIndex.value = 0
    }
}

const goRight = () => {
    if (canGoRight.value) {
        currentIndex.value = 1
    }
}

</script>


<template>
    <div class="exploreOthers">
        <h2 class="title">{{ title }}</h2>
        <div class="container" v-if="!customCategory">
            <template v-for="(item, index) in showTypes" :key="index">
                <NuxtLink 
                    v-if="item.path"
                    :to="localizedPath(item.path)"
                    class="model-part clickable"
                >
                    <img :src="item.icon" :alt="item.desc || 'Model icon'" v-if="item.icon" loading="lazy">
                    <div class="desc">{{ item.desc }}</div>
                </NuxtLink>
                <div v-else class="model-part">
                    <img :src="item.icon" :alt="item.desc || 'Model icon'" v-if="item.icon" loading="lazy">
                    <div class="desc">{{ item.desc }}</div>
                </div>
            </template>
        </div>
        <div v-else class="imgsContainer">
            <div 
                class="left arrow" 
                :class="{ disabled: !canGoLeft }"
                @click="goLeft"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" style="transform: rotate(180deg);">
                    <path d="M7.5 15L12.5 10L7.5 5" :stroke="canGoLeft ? 'white' : '#67676a'" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <div class="img-list">
                <div class="img-container" :style="{transform: `translateX(${-currentIndex * 320}px)`}">
                    <template v-for="(item, index) in showTypes" :key="index">
                        <NuxtLink 
                            v-if="item.path"
                            :to="localizedPath(item.path)"
                            class="model-cart clickable"
                        >
                            <img :src="item.bg" :alt="item.desc || 'Model background'" v-if="item.bg" loading="lazy">
                            <div class="desc">{{ item.desc }}</div>
                        </NuxtLink>
                        <div v-else class="model-cart">
                            <img :src="item.bg" :alt="item.desc || 'Model background'" v-if="item.bg" loading="lazy">
                            <div class="desc">{{ item.desc }}</div>
                        </div>
                    </template>
                </div>
            </div>
            <div 
                class="right arrow" 
                :class="{ disabled: !canGoRight }"
                @click="goRight"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" :stroke="canGoRight ? 'white' : '#67676a'" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
        </div>
    </div>
</template>

<style scoped lang="less">
.exploreOthers {
    margin-bottom: 240px;

    .title {
        font-size: 3.75rem;
        font-weight: 700;
        margin-bottom: 60px;
        color: #FFFFFF;
    }

    .container {
        display: flex;
        justify-content: center;
        column-gap: 20px;
        margin:0 auto;

        .model-part {
            padding: 20px 30px;
            border-radius: 10px;
            background-color: rgba(255, 255, 255, 0.3);
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: space-around;

            img {
                width: 3.75rem;
                height: 3.75rem;
            }

            .desc {
                color: #FFFFFF;
            }

            &.clickable {
                cursor: pointer;

                &:hover {
                    transform: translateY(-5px);
                    background-color: rgba(255, 255, 255, 0.4);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                }
            }
        }
    }

    @media (max-width: 1024px) {
        margin-bottom: 150px;

        .title {
            font-size: 2.5rem;
            margin-bottom: 40px;
        }

        .container {
            flex-wrap: wrap;
            column-gap: 15px;
            row-gap: 15px;
            padding: 0 20px;
        }
    }

    @media (max-width: 768px) {
        margin-bottom: 100px;

        .title {
            font-size: 2rem;
            margin-bottom: 30px;
            padding: 0 16px;
        }

        .container {
            flex-direction: column;
            align-items: center;
            column-gap: 0;
            row-gap: 12px;
            padding: 0 16px;

            .model-part {
                width: 100%;
                max-width: 400px;
                padding: 16px 24px;

                img {
                    width: 3rem;
                    height: 3rem;
                }
            }
        }
    }

    @media (max-width: 480px) {
        margin-bottom: 80px;

        .title {
            font-size: 1.5rem;
            margin-bottom: 24px;
        }

        .container {
            .model-part {
                padding: 14px 20px;

                img {
                    width: 2.5rem;
                    height: 2.5rem;
                }

                .desc {
                    font-size: 0.875rem;
                }
            }
        }
    }
}

.imgsContainer {
    display: flex;
    align-items: center;
    max-width: 1100px;
    margin: 0 auto;
    overflow-x: hidden;
    justify-content: space-between;

    .arrow {
        width: 30px;
        height: 30px;
        background-color: #333643;
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(.disabled) {
            background-color: #444755;
        }

        &.disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    @media (max-width: 768px) {
        padding: 0 12px;

        .arrow {
            width: 28px;
            height: 28px;
            flex-shrink: 0;
        }
    }

    @media (max-width: 480px) {
        .arrow {
            width: 24px;
            height: 24px;
            font-size: 0.875rem;
        }
    }
}

.img-list{
    overflow-x: hidden;
    width: 1000px;

    .img-container{
        display: flex;
        /* justify-content: space-between; */
        column-gap: 14px;
        margin: 0 auto;
        width: 100%;
        transition: transform 0.5s ease-in-out;
    }

    .model-cart {
        flex-shrink: 0;
        cursor: pointer;
        transition: transform 0.3s ease;
        
        &:hover {
            transform: translateY(-5px);
        }
        
        img {
            width: 320px;
            height: 200px;
            border-radius: 16px;
            object-fit: cover;
        }

        .desc {
            color: #FFFFFF;
        }
    }

    @media (max-width: 768px) {
        width: calc(100vw - 100px);
        max-width: 600px;

        .img-container {
            column-gap: 10px;
        }

        .model-cart {
            img {
                width: 240px;
                height: 150px;
                border-radius: 12px;
                object-fit: cover;
            }
        }
    }

    @media (max-width: 480px) {
        width: calc(100vw - 80px);

        .img-container {
            column-gap: 8px;
        }

        .model-cart {
            img {
                width: 200px;
                height: 125px;
                object-fit: cover;
            }

            .desc {
                font-size: 0.875rem;
            }
        }
    }
}
</style>
